import React from 'react';
import classNames from 'classnames';
import { Icon } from '../../atoms/icon/icon';
import { useAutoControlled } from '../../hooks';
import * as utils from '../../utils';
import {
    SideNavigationHeader,
    SideNavigationHeaderProps,
} from './parts/sideNavigationHeader';

interface SubItem {
    label: string;
    url: string;
    isDisabled?: boolean;
}

interface MenuItem {
    label: string;
    icon: string;
    url?: string;
    isDisabled?: boolean;
    isOpen?: boolean;
    subItems?: SubItem[];
}

interface SideNavigationProps extends React.HTMLAttributes<HTMLElement> {
    menuItems?: MenuItem[];
    header?: SideNavigationHeaderProps;
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
}

/**
 *
 * @name m-side-navigation
 * @type molecule
 * @copyright Robert Bosch GmbH
 *
 * @param {object|function} header      The side navigation header.
 * @param {MenuItem[]} menuItems        Menu items to display.
 *
 * @description
 * representation of side navigation
 */

const SideNavigation = React.forwardRef<HTMLElement, SideNavigationProps>(
    (
        {
            header,
            menuItems,
            className,
            open,
            defaultOpen,
            onOpenChange,
            children,
            ...rest
        },
        forwardedRef,
    ) => {
        const [isOpen, setOpen] = useAutoControlled({
            defaultValue: defaultOpen,
            value: open,
            initialValue: false,
        });

        const [isOpenSubItem, setOpenSubItem] = useAutoControlled({
            defaultValue: defaultOpen,
            value: open,
            initialValue: false,
        });

        const classes = classNames('m-side-navigation', {
            [className]: className,
            '-open': isOpen,
        });

        function handleOpenChange(state: boolean) {
            if (typeof onOpenChange === 'function') {
                onOpenChange(state);
            }
            if (typeof header?.onOpenChange === 'function') {
                utils.invoke(header, 'onOpenChange', state);
            }
            setOpen(state);
        }

        function handleOpenSubItem(index: number) {
            setOpenSubItem(
                prevState => ([...menuItems][index].isOpen = !prevState),
            );
        }

        return (
            <nav className={classes} {...rest} ref={forwardedRef}>
                <SideNavigationHeader
                    {...header}
                    open={open}
                    onOpenChange={handleOpenChange}
                />
                <ul className="m-side-navigation__menuItems" role="menubar">
                    {children ||
                        menuItems.map((menuItem, index) =>
                            menuItem.subItems ? (
                                <li
                                    key={index}
                                    className={classNames(
                                        'm-side-navigation__menuItem',
                                        {
                                            '-disabled':
                                                menuItem.isDisabled === true,
                                            '-open': menuItem.isOpen === true,
                                            '-closed':
                                                menuItem.isOpen === false,
                                        },
                                    )}
                                    role="none"
                                >
                                    <button
                                        type="button"
                                        key={index}
                                        className="m-side-navigation__group"
                                        tabIndex={
                                            menuItem.isDisabled ? -1 : null
                                        }
                                    >
                                        <Icon
                                            iconName={menuItem.icon}
                                            title={menuItem.label}
                                        />
                                        <span className="m-side-navigation__label">
                                            {menuItem.label}
                                        </span>
                                        <Icon
                                            iconName="down"
                                            title="down"
                                            className="arrow"
                                            onClick={() =>
                                                handleOpenSubItem(index)
                                            }
                                        />
                                    </button>
                                    <ul
                                        className="m-side-navigation__menuSubitems"
                                        role="menu"
                                    >
                                        {menuItem.subItems.map(
                                            ({ url, label, isDisabled }, i) => {
                                                return (
                                                    <li
                                                        key={i}
                                                        className={`m-side-navigation__menuSubitem ${
                                                            isDisabled === true
                                                                ? '-disabled'
                                                                : ''
                                                        }`}
                                                        role="none"
                                                    >
                                                        <a
                                                            href={url}
                                                            key={i}
                                                            role="menuitem"
                                                            className="m-side-navigation__link"
                                                            aria-disabled={
                                                                isDisabled
                                                            }
                                                            tabIndex={-1}
                                                        >
                                                            <span className="m-side-navigation__label">
                                                                {label}
                                                            </span>
                                                        </a>
                                                    </li>
                                                );
                                            },
                                        )}
                                    </ul>
                                </li>
                            ) : (
                                <li
                                    className={`m-side-navigation__menuItem ${
                                        menuItem.isDisabled === true
                                            ? '-disabled'
                                            : ''
                                    }`}
                                    key={index}
                                    role="none"
                                >
                                    <a
                                        href={menuItem.url}
                                        role="menuitem"
                                        className="m-side-navigation__link"
                                        tabIndex={
                                            menuItem.isDisabled ? -1 : null
                                        }
                                    >
                                        <Icon
                                            iconName={menuItem.icon}
                                            title={menuItem.label}
                                        />
                                        <span className="m-side-navigation__label">
                                            {menuItem.label}
                                        </span>
                                    </a>
                                </li>
                            ),
                        )}
                </ul>
            </nav>
        );
    },
);

SideNavigation.displayName = 'SideNavigation';

export { SideNavigation };
export type { SideNavigationProps };
